import { StopeLocationDto } from 'providers/api';
import PCNode from './PCNode';
import Vector from './Vector';

export const BOOSTER_SUFFIX = '_booster';

export const CHOKE_SUFFIX = '_choke';

export const isBoosterNode = (nodeId: string): boolean => nodeId.endsWith(BOOSTER_SUFFIX);

export const getOriginalBoosterNodeId = (boosterNodeId: string): string => boosterNodeId.replace(BOOSTER_SUFFIX, '');

export const isChokeNode = (nodeId: string): boolean => nodeId.endsWith(CHOKE_SUFFIX);

export const getOriginalChokeNodeId = (chokeNodeId: string): string => chokeNodeId.replace(CHOKE_SUFFIX, '');

// Booster and choke twins are synthetic nodes injected at the coordinates of their original node purely to
// create a pressure discontinuity on the HGL. They must never take part in the routing graph.
export const isSyntheticNodeId = (nodeId: string): boolean => isBoosterNode(nodeId) || isChokeNode(nodeId);

const createBoosterNodes = (nodes: Record<string, PCNode>): Record<string, PCNode> => {
  const updatedNodes: Record<string, PCNode> = { ...nodes };

  Object.values(nodes).forEach((node) => {
    if (node.boosterPressure > 0) {
      const boosterSuffixId = `${node.id}${BOOSTER_SUFFIX}`;

      updatedNodes[boosterSuffixId] = new PCNode({
        coords: new Vector(node.coords.x, node.coords.y, node.coords.z),
        id: boosterSuffixId,
        pipeId: node.pipeId,
        area: node.area,
        level: node.level,
        pit: node.pit,
        nodeColour: node.nodeColour,
        nodeSize: node.nodeSize,
      });
    }
  });

  return updatedNodes;
};

const createChokeNodes = (nodes: Record<string, PCNode>): Record<string, PCNode> => {
  const updatedNodes: Record<string, PCNode> = { ...nodes };

  Object.values(nodes).forEach((node) => {
    if (node.chokeHead > 0 && !isChokeNode(node.id)) {
      const chokeSuffixId = `${node.id}${CHOKE_SUFFIX}`;

      updatedNodes[chokeSuffixId] = new PCNode({
        coords: new Vector(node.coords.x, node.coords.y, node.coords.z),
        id: chokeSuffixId,
        pipeId: node.pipeId,
        area: node.area,
        level: node.level,
        pit: node.pit,
        nodeColour: node.nodeColour,
        nodeSize: node.nodeSize,
        chokeHead: node.chokeHead,
      });
    }
  });

  return updatedNodes;
};

const initNodes = (reticulationData: any): Record<string, PCNode> => {
  const nodeObjs: Record<string, PCNode> = {};
  if (typeof reticulationData === 'undefined' || !reticulationData?.nodes) return nodeObjs;

  const isStopeNodeId = new Set<string>((reticulationData.stopeLocations || []).map((sl: StopeLocationDto) => sl.nodeId));

  const toNumber = (val: unknown, name: string, nodeId: string) => {
    const num = parseFloat(String(val));
    if (Number.isNaN(num)) {
      console.error(`Node ${nodeId}: Invalid ${name} - expected number but got ${val}`);
      return null;
    }
    return num;
  };

  reticulationData.nodes.forEach((node: any) => {
    const { nodeId } = node;
    const { pipeId } = node;
    if (!nodeId || typeof nodeId !== 'string') {
      console.error(`Node ${nodeId || 'unknown'}: Missing or invalid nodeId`);
      return;
    }
    if (!pipeId || typeof pipeId !== 'string') {
      console.error(`Node ${nodeId}: Missing or invalid pipeId`);
      return;
    }

    const x = toNumber(node.point?.x, 'x coordinate', nodeId);
    const y = toNumber(node.point?.y, 'y coordinate', nodeId);
    const z = toNumber(node.point?.z, 'z coordinate', nodeId);
    if (x === null || y === null || z === null) return;

    nodeObjs[nodeId] = new PCNode({
      coords: new Vector(x, y, z),
      id: nodeId,
      pipeId,
      nodeName: node.nodeName,
      isStope: isStopeNodeId.has(nodeId),
      isPit: !!node.isPit,
      isPlantNode: !!node.isPlantNode,
      area: node.area,
      level: node.level,
      pit: node.pit,
      boosterPressure: node.boosterPressure,
      ruptureDiskPressure: node.ruptureDiskPressure,
      chokeHead: node.chokeHead,
      nodeColour: node.nodeColour,
      nodeSize: node.nodeSize,
      isManuallyAdded: node.isManuallyAdded,
    });
  });

  const nodesWithBoosters = createBoosterNodes(nodeObjs);
  return createChokeNodes(nodesWithBoosters);
};

export default initNodes;
